import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import AtelierMap from '@/components/atelier/AtelierMap';
import Button from '@/components/atelier/Button';
import AnimatedDot from '@/components/atelier/AnimatedDot';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import SectionHeader from '@/components/atelier/SectionHeader';
import BookingTier from '@/components/atelier/BookingTier';
import BeforeAfterCard from '@/components/atelier/BeforeAfterCard';
import DynamicServiceFAQ from '@/components/services/DynamicServiceFAQ';
import LandingLongFormSection from '@/components/atelier/LandingLongFormSection';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import EditableHeroImage from '@/components/atelier/EditableHeroImage';
import NotFoundPage from '@/components/routing/NotFoundPage';
import { seoConfig, updateMetaTags, getCanonicalUrl } from '@/lib/seo';
import { BUSINESS } from '@/lib/businessConfig';
import { getAreaNameBySlug } from '@/lib/areaLinks';
import { cmsPath, setHreflangPair, twinPath } from '@/lib/cmsLang';
import LangSwitch from '@/components/routing/LangSwitch';

const FALLBACK_BOOKING_URL = BUSINESS.bookingUrl;

const CATEGORY_GRADIENTS = {
  'hair-color': 'linear-gradient(165deg, #d4b88a 0%, #a98a5f 35%, #5D4A3B 70%, #1C1814 100%)',
  'haircuts': 'linear-gradient(165deg, #c4a87c, #5D4A3B 60%, #1C1814)',
  'hair-treatments': 'linear-gradient(165deg, #ede2cc, #a98a5f 50%, #5D4A3B)',
  'face-metrics': 'linear-gradient(165deg, #3a2e22, #5D4A3B 50%, #c4a87c)',
  'location-pages': 'linear-gradient(165deg, #5D4A3B, #3a2e22, #1C1814)',
};

const CATEGORY_LABELS = {
  'hair-color': 'Χρώμα',
  'haircuts': 'Κούρεμα',
  'hair-treatments': 'Θεραπεία',
  'face-metrics': 'AI Hair Analysis',
  'location-pages': 'Premium Salon',
};

const CATEGORY_LABELS_EN = {
  'hair-color': 'Colour',
  'haircuts': 'Haircut',
  'hair-treatments': 'Treatment',
  'face-metrics': 'AI Hair Analysis',
  'location-pages': 'Premium Salon',
};

const TIER_LEVELS = ['i', 'ii', 'iii', 'iv'];

// Page-level UI strings. Greek strings are the existing ones, unchanged.
const T = {
  el: {
    from: 'Από', duration: 'Ενδεικτική διάρκεια', minutes: 'λεπτά', category: 'Κατηγορία',
    book: 'Κράτηση Online →', allServices: 'Όλες οι Υπηρεσίες', allServicesHref: '/services', faqJump: 'Συχνές ερωτήσεις ↓',
    tierFrom: 'από', whoFor: 'Για ποιον είναι',
    longEyebrow: 'Σε βάθος', longTitle: 'Αναλυτικά', longTitleEm: 'για την υπηρεσία.',
    benefits: ['Τι περιλαμβάνει', 'All-inclusive', 'εμπειρία.', 'Κάθε λεπτομέρεια μετράει. Όλα όσα κάνουν την επίσκεψή σου ξεχωριστή — στην τιμή.'],
    process: ['Η Διαδικασία', 'Βήμα προς βήμα,', 'με προσοχή.', 'Κάθε επίσκεψη ακολουθεί συγκεκριμένη μεθοδολογία για να σου εξασφαλίσει το καλύτερο αποτέλεσμα.'],
    results: ['Transformations', 'Δες τα', 'αποτελέσματα.', 'Πραγματικοί πελάτες, πραγματικά αποτελέσματα από το atelier μας.'],
    tiers: ['Επίπεδα εξειδίκευσης', 'Διάλεξε τον', 'δικό σου ειδικό.', 'Από Colorist μέχρι Master Specialist, ο καθένας ειδικός σε ένα επίπεδο τέχνης. Όλα all-inclusive.'],
    tierCta: 'Επιλογή',
    related: ['Σχετικές Υπηρεσίες', 'Εξερεύνησε', 'περισσότερα.', 'Άλλες υπηρεσίες του atelier που πιθανότατα θα σε ενδιαφέρουν.'],
    faq: ['Συχνές Ερωτήσεις', 'Ό,τι θέλεις', 'να μάθεις.', 'Συγκεντρώσαμε τις πιο συχνές ερωτήσεις για να σε βοηθήσουμε να αποφασίσεις.'],
    cta: ['Έτοιμη να', 'ξεκινήσεις;', 'Κλείσε ραντεβού online σε 60 δευτερόλεπτα. Επιλογή tier, ευέλικτες ώρες, secure booking.'],
    notFound: 'Η υπηρεσία που ψάχνεις δεν υπάρχει ή έχει μετακινηθεί.',
  },
  en: {
    from: 'From', duration: 'Approx. duration', minutes: 'min', category: 'Category',
    book: 'Book now →', allServices: 'All services in English', allServicesHref: '/en', faqJump: 'FAQ ↓',
    tierFrom: 'from', whoFor: 'Who it is for',
    longEyebrow: 'In depth', longTitle: 'Everything', longTitleEm: 'about this service.',
    benefits: ['What is included', 'An all-inclusive', 'experience.', 'Every detail matters — everything that makes your visit special is included in the price.'],
    process: ['The process', 'Step by step,', 'with care.', 'Every visit follows a clear method, so you get the best possible result.'],
    results: ['Transformations', 'See the', 'results.', 'Real clients, real results from our atelier.'],
    tiers: ['Levels of expertise', 'Choose your', 'specialist.', 'From Colorist to Master Specialist, each an expert at their level. All-inclusive pricing.'],
    tierCta: 'Book now',
    related: ['Related services', 'Explore', 'further.', 'Other atelier services you may find useful.'],
    faq: ['FAQ', 'Everything you', 'want to know.', 'The questions we are asked most often, answered to help you decide.'],
    cta: ['Ready to', 'begin?', 'Book online in 60 seconds. Choose your specialist, flexible times, secure booking. English spoken.'],
    notFound: 'The page you are looking for does not exist or has moved.',
  },
};

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return 'https://ikonomakis.gr/';
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  return `https://ikonomakis.gr${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

function buildStructuredData(page, lang = 'el') {
  const en = lang === 'en';
  const pageUrl = en ? absoluteUrl(cmsPath(page.slug, 'en')) : (page.canonical_url || absoluteUrl(`/${page.slug}`));
  const isPersonPage = ['thodoris-ikonomakis-hair-expert', 'hair-expert-greece'].includes(page.slug);
  const isAreaPage = page.slug?.startsWith('kommotirio-');
  const areaName = isAreaPage ? getAreaNameBySlug(page.slug) : null;
  const serviceSchema = isPersonPage ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Thodoris Ikonomakis',
    alternateName: 'Θοδωρής Οικονομάκης',
    jobTitle: 'Hair Expert, Founder of Hair Community Greece, Creator of Face Metrics Pro',
    worksFor: {
      '@type': 'Organization',
      name: 'Ikonomakis Hair Atelier',
      url: 'https://ikonomakis.gr'
    },
    url: pageUrl,
    knowsAbout: ['Haircutting', 'Hair Color', 'Balayage', 'Face Shape Analysis', 'Hair Consultation', 'Hair Education', 'Hair Technology'],
    sameAs: ['https://www.instagram.com/ikonomakis_hairexpert/', 'https://www.instagram.com/haircommunitygreece/']
  } : {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${pageUrl}#service`,
    name: page.title,
    serviceType: page.title,
    description: page.meta_description || page.hero_subtitle || page.intro_paragraph,
    url: pageUrl,
    image: page.hero_image_url,
    provider: { '@id': `${BUSINESS.siteUrl}/#salon` },
    // Area pages: the page's own locality first, so each geo page signals its distinct area
    areaServed: en ? [
      { '@type': 'City', name: 'Chalandri' },
      { '@type': 'City', name: 'Athens' },
      { '@type': 'AdministrativeArea', name: 'Northern Suburbs of Athens' },
    ] : [
      ...(isAreaPage && areaName ? [{ '@type': 'Place', name: areaName }] : []),
      { '@type': 'City', name: 'Χαλάνδρι' },
      { '@type': 'City', name: 'Αθήνα' },
      { '@type': 'AdministrativeArea', name: 'Βόρεια Προάστια Αθήνας' },
    ],
    // No aggregateRating here: Google's review-snippet spec does not accept ratings on
    // Service, and a self-serving rating on a service page fails validation. The salon's
    // verified rating lives once on the HairSalon node (#salon) instead.
    offers: page.starting_price && !isAreaPage ? {
      '@type': 'Offer',
      price: page.starting_price,
      priceCurrency: 'EUR',
      priceSpecification: { '@type': 'PriceSpecification', minPrice: page.starting_price, priceCurrency: 'EUR' },
      availability: 'https://schema.org/InStock',
      url: page.booking_links?.find((b) => b.primary)?.fresha_url || page.booking_links?.[0]?.fresha_url || pageUrl
    } : undefined
  };

  const breadcrumbSchema = page.breadcrumb_path?.length ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: page.breadcrumb_path.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      item: absoluteUrl(crumb.url)
    }))
  } : null;

  const webPageSchema = en ? {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: page.meta_title || page.title,
    description: page.meta_description || page.hero_subtitle || page.intro_paragraph,
    inLanguage: 'en',
    isPartOf: { '@id': `${BUSINESS.siteUrl}/#website` },
    about: { '@id': `${pageUrl}#service` },
  } : null;

  return [webPageSchema, serviceSchema, breadcrumbSchema].filter(Boolean);
}

export default function LandingPageView({ initialPage = null, lang: langProp, alternate = null }) {
  const params = useParams();
  const lang = langProp || (initialPage?.lang === 'en' ? 'en' : 'el');
  const en = lang === 'en';
  const t = T[lang];
  // Native routes (/best-hair-salon-athens …) have no :slug param — the bound
  // record's slug is authoritative there; the URL is the fallback.
  const slug = initialPage?.slug || params.slug || window.location.pathname.replace(/^\/(en\/)?/, '').split('?')[0];
  const selfPath = cmsPath(slug, lang);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(!initialPage);
  const [notFound, setNotFound] = useState(false);
  const staticCfg = seoConfig[selfPath] || null;
  // Interim H1 while the record loads — the page's own topic, from its curated title.
  const interimHeading = (initialPage?.title) || (staticCfg?.title || '').split(/\s[|–—]\s/)[0] || null;

  // First paint, before the record resolves: correct title, description and canonical.
  useLayoutEffect(() => {
    const pathname = selfPath;
    const title = initialPage?.meta_title || initialPage?.title || staticCfg?.title;
    const description = initialPage?.meta_description || staticCfg?.description;
    if (title) document.title = title;
    if (description) {
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', description);
    }
    const canonical = en ? getCanonicalUrl(pathname) : (staticCfg?.canonical || initialPage?.canonical_url || getCanonicalUrl(pathname));
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);
  }, [slug]);

  useEffect(() => {
    if (initialPage && initialPage.slug === slug) { setPage(initialPage); setLoading(false); return; }
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    base44.functions.invoke('getPublicLandingPage', { slug, lang })
      .then((response) => {
        if (response.data?.page && (response.data.page.lang === 'en' ? 'en' : 'el') === lang) {
          setPage(response.data.page);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug, initialPage, lang]);
  
  // SEO meta — curated static registry (seo.js) wins when an entry exists, else the
  // record's own fields. Full set: title, description, robots, canonical, OG, Twitter.
  useEffect(() => {
    if (!page) return;
    const pathname = cmsPath(page.slug, lang);
    const cfg = seoConfig[pathname] || {};
    const selfUrl = getCanonicalUrl(pathname);
    // EN twins always canonicalise to /en/<slug>.
    const canonical = en ? selfUrl : (cfg.canonical || page.canonical_url || selfUrl);
    updateMetaTags({
      title: cfg.title || page.meta_title || page.title,
      description: cfg.description || page.meta_description || page.hero_subtitle || '',
      image: page.hero_image_url || cfg.image,
      url: selfUrl,
      type: 'website',
      canonical: canonical !== selfUrl ? canonical : null,
    });
    // hreflang for CMS twins (covers records created after the last build too).
    const twin = twinPath(page, lang, alternate);
    if (twin) {
      if (en) setHreflangPair(twin, pathname);
      else if (twin.startsWith('/en/')) setHreflangPair(pathname, twin);
    }
  }, [page, lang, alternate]);

  if (loading) {
    return (
      <div className="bg-cream-bg text-warm-black min-h-screen">
        <TopBar />
        <Nav />
        <section className="px-[6vw] pt-[70px] pb-[100px]">
          <div className="max-w-container mx-auto">
            <h1 className="font-serif text-display-xl text-warm-black font-medium tracking-[-3px] leading-[0.92] mb-6">
              {interimHeading || 'Ikonomakis Hair Atelier'}
            </h1>
            {staticCfg?.description && (
              <p className="text-body-l text-muted leading-[1.7] max-w-reading">{staticCfg.description}</p>
            )}
          </div>
        </section>
        <Footer />
      </div>
    );
  }
  
  if (notFound || !page) {
    return <NotFoundPage message={t.notFound} />;
  }
  
  const primaryBooking = page.booking_links?.find((b) => b.primary) || page.booking_links?.[0];
  const primaryBookingUrl = primaryBooking?.fresha_url || FALLBACK_BOOKING_URL;
  const heroGradient = CATEGORY_GRADIENTS[page.service_category] || CATEGORY_GRADIENTS['hair-color'];
  const categoryLabel = (en ? CATEGORY_LABELS_EN : CATEGORY_LABELS)[page.service_category];
  const structuredData = buildStructuredData(page, lang);
  const twin = twinPath(page, lang, alternate);
  const switchHref = twin && (en ? !twin.startsWith('/en/') : twin.startsWith('/en/')) ? twin : null;
  
  return (
    <div className="bg-cream-bg text-warm-black" lang={en ? 'en' : undefined}>
      <TopBar />
      <Nav />
      {en && (
        <div className="bg-warm-black text-cream text-tiny px-[6vw] py-2 flex justify-between flex-wrap gap-2">
          <span>English version{switchHref && <> · <a href={switchHref} hrefLang="el" lang="el" className="text-gold-light underline">Ελληνική έκδοση →</a></>}</span>
          <span>All staff speak English · Metro Line 3 → Chalandri</span>
        </div>
      )}
      {structuredData.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      
      {/* BREADCRUMBS */}
      {page.breadcrumb_path && page.breadcrumb_path.length > 0 && (
        <div className="px-[6vw] py-4 bg-cream-soft/40 border-b border-brown/10">
          <div className="max-w-container mx-auto">
            <nav aria-label={en ? 'Breadcrumb' : undefined} className="flex items-center gap-2 text-tiny text-muted/80 flex-wrap">
              {page.breadcrumb_path.map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-muted/40">/</span>}
                  {i === page.breadcrumb_path.length - 1 ? (
                    <span className="text-warm-black font-medium">{crumb.label}</span>
                  ) : (
                    <a href={crumb.url} className="hover:text-warm-black transition-colors">
                      {crumb.label}
                    </a>
                  )}
                </React.Fragment>
              ))}
              {!en && <LangSwitch lang={lang} href={switchHref} />}
            </nav>
          </div>
        </div>
      )}
      {!en && switchHref && !(page.breadcrumb_path?.length > 0) && (
        <div className="px-[6vw] py-3 bg-cream-soft/40 border-b border-brown/10">
          <div className="max-w-container mx-auto flex"><LangSwitch lang={lang} href={switchHref} /></div>
        </div>
      )}
      
      {/* HERO */}
      <section className="px-[6vw] pt-[70px] pb-[100px] overflow-hidden">
        <div className="max-w-container mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div>
            {page.hero_label && (
              <EyebrowPill variant="dark" className="mb-7">{page.hero_label}</EyebrowPill>
            )}
            <h1 className="font-serif text-display-xl text-warm-black font-medium tracking-[-3px] leading-[0.92] mb-6">
              {page.title}
            </h1>
            {page.hero_subtitle && (
              <p className="font-serif italic text-[24px] md:text-[28px] text-gold leading-[1.3] mb-9 max-w-[540px]">
                {page.hero_subtitle}
              </p>
            )}
            
            <div className="flex flex-wrap gap-6 items-center mb-9 pb-9 border-b border-brown/15">
              {page.starting_price != null && (
                <div>
                  <div className="text-tiny uppercase tracking-wider text-muted mb-1">{t.from}</div>
                  <div className="font-serif text-[28px] text-warm-black font-medium leading-none">€{page.starting_price}</div>
                </div>
              )}
              {page.duration_minutes && (
                <div>
                  <div className="text-tiny uppercase tracking-wider text-muted mb-1">{t.duration}</div>
                  <div className="font-serif text-[28px] text-warm-black font-medium leading-none">{page.duration_minutes} {t.minutes}</div>
                </div>
              )}
              {categoryLabel && (
                <div>
                  <div className="text-tiny uppercase tracking-wider text-muted mb-1">{t.category}</div>
                  <div className="font-serif text-[18px] text-warm-black font-medium leading-none pt-2">{categoryLabel}</div>
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Button variant="primary" href={primaryBookingUrl} target="_blank" rel="noopener noreferrer">
                {t.book}
              </Button>
              <Button variant="ghost" href={t.allServicesHref}>{t.allServices}</Button>
              {page.faq_items?.length > 0 && <Button variant="ghost" href="#service-faq">{t.faqJump}</Button>}
            </div>
          </div>
          
          <div className="relative aspect-[4/5] max-h-[680px]">
            <EditableHeroImage
              page={page}
              heroGradient={heroGradient}
              onImageChange={(hero_image_url) => setPage((current) => ({ ...current, hero_image_url }))}
            />
            
            {primaryBooking && (
              <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-ivory rounded-card p-5 shadow-card max-w-[280px]">
                <div className="text-eyebrow uppercase tracking-wider text-gold mb-1 font-medium">Featured Tier</div>
                <div className="font-serif text-body font-medium text-warm-black mb-1 leading-tight">{primaryBooking.tier_or_variant}</div>
                <div className="text-tiny text-muted">{t.tierFrom} €{primaryBooking.price_from}</div>
              </div>
            )}
            
            <div className="absolute -top-4 -right-4 lg:-right-6 bg-warm-black text-gold-light rounded-pill px-5 py-2.5 rotate-[8deg] shadow-card flex items-center gap-2">
              <AnimatedDot color="gold" size={8} />
              <span className="text-tiny uppercase tracking-wider font-medium">All-Inclusive</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* INTRO */}
      {page.intro_paragraph && (
        <section className="px-[6vw] py-[100px] bg-cream-soft/50">
          <div className="max-w-editorial mx-auto text-center">
            <p className="font-serif text-[22px] md:text-[26px] text-warm-black leading-[1.55] tracking-tight">
              {page.intro_paragraph}
            </p>
          </div>
        </section>
      )}
      
      {/* LONG-FORM BODY */}
      <LandingLongFormSection text={page.description} {...(en ? { eyebrow: t.longEyebrow, title: t.longTitle, titleEm: t.longTitleEm } : {})} />

      {/* WHO FOR */}
      {page.who_for && (
        <section className="px-[6vw] py-[100px]">
          <div className="max-w-container-narrow mx-auto">
            <div className="bg-warm-black text-cream rounded-card-xl p-10 lg:p-16 relative overflow-hidden">
              <div 
                className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none opacity-30"
                style={{ background: 'radial-gradient(circle, rgba(196,168,124,0.4), transparent 60%)' }}
              />
              <div className="relative">
                <EyebrowPill variant="cream-on-dark" className="mb-6">{t.whoFor}</EyebrowPill>
                <p className="font-serif text-display-s text-cream leading-[1.25] tracking-[-1.2px] font-medium">
                  {page.who_for}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* BENEFITS */}
      {page.benefits && page.benefits.length > 0 && (
        <section className="px-[6vw] py-[120px]">
          <div className="max-w-container mx-auto">
            <SectionHeader
              eyebrow={t.benefits[0]}
              title={t.benefits[1]}
              titleEm={t.benefits[2]}
              subtitle={t.benefits[3]}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.benefits.map((b, i) => (
                <div key={i} className="bg-ivory rounded-card-l p-7 hover:shadow-card-soft hover:-translate-y-1 transition-all duration-400">
                  <div className="text-gold text-[24px] mb-4">✦</div>
                  <h3 className="font-serif text-[22px] text-warm-black mb-3 font-medium leading-tight">{b.title}</h3>
                  <p className="text-body-s text-muted leading-[1.6]">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* PROCESS STEPS */}
      {page.process_steps && page.process_steps.length > 0 && (
        <section className="bg-cream-soft px-[6vw] py-[120px]">
          <div className="max-w-container-narrow mx-auto">
            <SectionHeader
              eyebrow={t.process[0]}
              title={t.process[1]}
              titleEm={t.process[2]}
              subtitle={t.process[3]}
            />
            <div className="space-y-3">
              {[...page.process_steps].sort((a,b) => (a.step_number || 0) - (b.step_number || 0)).map((s, i) => (
                <div key={i} className="bg-ivory rounded-card-l p-6 lg:p-8 grid grid-cols-[auto_1fr] gap-6 lg:gap-8 items-center hover:shadow-card-soft transition-all duration-400">
                  <div className="font-serif italic text-display-s text-gold leading-none">
                    {String(s.step_number || (i+1)).padStart(2, '0')}
                  </div>
                  <div>
                    <h3 className="font-serif text-[22px] text-warm-black mb-2 font-medium leading-tight">{s.title}</h3>
                    <p className="text-body-s text-muted leading-[1.6]">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* BEFORE / AFTER */}
      {page.before_after_images && page.before_after_images.length > 0 && (
        <section className="px-[6vw] py-[120px]">
          <div className="max-w-container mx-auto">
            <SectionHeader
              eyebrow={t.results[0]}
              title={t.results[1]}
              titleEm={t.results[2]}
              subtitle={t.results[3]}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.before_after_images.map((ba, i) => (
                <BeforeAfterCard 
                  key={i}
                  beforeImage={ba.before_url ? `url(${ba.before_url}) center/cover` : undefined}
                  afterImage={ba.after_url ? `url(${ba.after_url}) center/cover` : undefined}
                  description={ba.caption}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* BOOKING TIERS */}
      {page.booking_links && page.booking_links.length > 0 && (
        <section className="bg-warm-black text-cream px-[6vw] py-[120px]">
          <div className="max-w-container-narrow mx-auto">
            <SectionHeader
              variant="light"
              eyebrow={t.tiers[0]}
              title={t.tiers[1]}
              titleEm={t.tiers[2]}
              subtitle={t.tiers[3]}
            />
            <div className="space-y-4">
              {page.booking_links.map((link, i) => (
                <BookingTier
                  key={i}
                  level={TIER_LEVELS[i] || `${i+1}`}
                  name={link.tier_or_variant}
                  description={link.label}
                  priceFrom={link.price_from}
                  href={link.fresha_url || FALLBACK_BOOKING_URL}
                  featured={Boolean(link.primary)}
                  featuredLabel="PRIMARY"
                  priceLabel={t.tierFrom}
                  ctaText={t.tierCta}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* INTERNAL LINKS */}
      {page.internal_links && page.internal_links.length > 0 && (
        <section className="px-[6vw] py-[120px]">
          <div className="max-w-container mx-auto">
            <SectionHeader
              eyebrow={t.related[0]}
              title={t.related[1]}
              titleEm={t.related[2]}
              subtitle={t.related[3]}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {page.internal_links.map((link, i) => (
                <a 
                  key={i}
                  href={link.url}
                  className="block bg-ivory rounded-card-l p-6 group hover:shadow-card hover:-translate-y-1 transition-all duration-400"
                >
                  <div className="text-gold text-[24px] mb-3 transition-transform group-hover:translate-x-1">→</div>
                  <h3 className="font-serif text-[18px] text-warm-black mb-2 font-medium leading-tight">{link.label}</h3>
                  <p className="text-tiny text-muted leading-[1.5]">{link.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* FAQ */}
      {page.faq_items && page.faq_items.length > 0 && (
        <section className="bg-cream-soft px-[6vw] py-[120px]">
          <div className="max-w-editorial mx-auto">
            <SectionHeader
              eyebrow={t.faq[0]}
              title={t.faq[1]}
              titleEm={t.faq[2]}
              subtitle={t.faq[3]}
              center
            />
            <DynamicServiceFAQ path={cmsPath(page.slug, lang)} items={page.faq_items} />
          </div>
        </section>
      )}
      
      {page.slug === 'kommotirio-voreia-proastia' && (
        <section className="px-[6vw] pt-[80px]">
          <div className="max-w-container-narrow mx-auto">
            <h2 className="font-serif text-warm-black mb-3">Πού θα μας βρείτε</h2>
            <p className="text-body text-muted mb-6">Ηρακλείτου 80, Χαλάνδρι 15238 · <a href="tel:+302118004132" className="text-gold underline underline-offset-2">211 800 4132</a></p>
            <AtelierMap />
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="px-[6vw] py-[120px]">
        <FinalCTACard
          title={t.cta[0]}
          titleEm={t.cta[1]}
          description={t.cta[2]}
          ctaText={t.book}
          ctaHref={primaryBookingUrl}
          ctaTarget="_blank"
          ctaRel="noopener noreferrer"
        />
      </section>
      
      <Footer />
    </div>
  );
}